import { useEffect, useMemo, useRef, useState } from "react"

import { debounce } from "lodash"
// pdfjsSetup replaces pdfjs-dist/webpack.mjs's worker auto-wiring. It loads the
// core library that sets globalThis.pdfjsLib, so — like webpack.mjs — it must be
// imported before pdf_viewer.
// @ts-ignore — side-effect-only .mjs module, no type declarations
import "./pdfjsSetup.mjs"
// The legacy/ builds carry pdf.js's own core-js polyfills for the modern JS APIs
// it calls unconditionally (iterator helpers, Math.sumPrecise, Uint8Array
// base64/hex, …). Safe Exam Browser embeds an older Chromium — 3.7 ships 121 —
// where those APIs do not exist, so the modern builds break there (issue #785).
// eslint-disable-next-line import/order
import * as pdfjs from "pdfjs-dist/legacy/build/pdf.mjs"
// eslint-disable-next-line import/order
import { EventBus, PDFFindController, PDFLinkService, PDFViewer } from "pdfjs-dist/legacy/web/pdf_viewer.mjs"

import { useTranslations } from "@cirrus/core/i18n"

import { createApi } from "./api"
import { ScaleDefaultInfo } from "./constants"
import { buildErrorReport, recordDiagnostic } from "./diagnostics"
import { IPdfViewerProps } from "./props"
import { ViewerState } from "./state"

export const useViewer = (props: IPdfViewerProps) => {

    const containerRef = useRef<any>()
    const pdfRef = useRef<any>()
    const pdfViewerRef = useRef<PDFViewer>()
    const [state, setState] = useState(new ViewerState())
    const { i18n } = useTranslations()

    const eventBus = useMemo(() => new EventBus(), [])

    const linkService = useMemo(() => new PDFLinkService({
        eventBus,
    }), [eventBus])

    const findController = useMemo(() => new PDFFindController({
        eventBus,
        linkService,
        updateMatchesCountOnProgress: true,
    }), [eventBus, linkService])

    const pagesLoaded = ({source, pagesCount}) => {
        if (!(source instanceof PDFViewer)) return

        source.currentScaleValue = ScaleDefaultInfo.defaultScale

        if (pagesCount && pagesCount > 0) {
            pageChanging({ pageNumber: 1 })
        }
    }

    const pageChanging = ({ pageNumber }) => {
        const { onChangePage } = props
        onChangePage && onChangePage(pageNumber)
    }

    // Both "updatefindmatchescount" (progressive totals while pages are being
    // scanned) and "updatefindcontrolstate" (find-next/prev moves `current`)
    // carry matchesCount: { current, total }.
    const matchesCountChanged = ({ matchesCount }) => {
        const { onMatchesCount } = props
        onMatchesCount && matchesCount && onMatchesCount(matchesCount)
    }

    const debouncedPageChanging = debounce((args) => pageChanging(args), 300)

    const loadDocument = () => {

        if (!props.src) return

        setState((prev) => ({
            ...prev,
            errorMessage: null,
            errorReport: null,
            pending: true,
        }))

        const loadingTask = pdfjs.getDocument({
            url: props.src,
            // pdfjs fetches these at runtime; they are copied to /pdf-assets
            // by copy-webpack-plugin (see configs/settings/plugins.js). wasmUrl
            // is required for JBIG2/JPEG2000 image decoding (otherwise images
            // render white); cMap/standardFont/icc improve text & colour fidelity.
            wasmUrl: "/pdf-assets/wasm/",
            cMapUrl: "/pdf-assets/cmaps/",
            cMapPacked: true,
            standardFontDataUrl: "/pdf-assets/standard_fonts/",
            iccUrl: "/pdf-assets/iccs/",
        })

        const doLoad = async function () {
            try {
                const pdfDocument = await loadingTask.promise
                pdfViewerRef.current?.setDocument(pdfDocument)
                linkService.setDocument(pdfDocument, null)
                props.onSetNumPages && props.onSetNumPages(pdfDocument.numPages)
                pdfRef.current = pdfDocument
                setState((prev) => ({
                    ...prev,
                    errorMessage: null,
                    pending: false,
                }))
            } catch (e: any) {
                console.error(e)
                pdfRef.current?.loadingTask?.destroy()
                setState((prev) => ({
                    ...prev,
                    errorMessage: i18n("Notification.Messages:ErrorOccurredDuringDataLoading"),
                    errorReport: buildErrorReport(e),
                    pending: false,
                }))
            }
        }

        setTimeout(doLoad, 100)
    }

    useEffect(() => {

        // pdf.js reports plenty of failures outside the getDocument promise —
        // asset fetches, worker teardown, rendering. Those never reach the catch
        // in doLoad, and in Safe Exam Browser there is no console to read them
        // from, so record them for the on-screen report (issue #785).
        const onWindowError = (event: ErrorEvent) => {
            recordDiagnostic(`uncaught: ${event.message} @ ${event.filename || "?"}:${event.lineno || 0}`)
        }

        const onRejection = (event: PromiseRejectionEvent) => {
            const { reason } = event

            recordDiagnostic(`unhandled rejection: ${reason?.name || ""} ${reason?.message || String(reason)}`)
        }

        window.addEventListener("error", onWindowError)
        window.addEventListener("unhandledrejection", onRejection)

        eventBus.on("pagesloaded", pagesLoaded)
        eventBus.on("pagechanging", debouncedPageChanging)
        eventBus.on("updatefindmatchescount", matchesCountChanged)
        eventBus.on("updatefindcontrolstate", matchesCountChanged)

        const pdfViewer = new PDFViewer({
            eventBus,
            linkService,
            findController,
            container: containerRef.current,
        })

        linkService.setViewer(pdfViewer)

        if (props.apiRef) {
            props.apiRef.current = createApi(eventBus, pdfViewer)
        }

        pdfViewerRef.current = pdfViewer

        loadDocument()

        return () => {
            window.removeEventListener("error", onWindowError)
            window.removeEventListener("unhandledrejection", onRejection)
            eventBus.off("pagesloaded", pagesLoaded)
            eventBus.off("pagechanging", debouncedPageChanging)
            eventBus.off("updatefindmatchescount", matchesCountChanged)
            eventBus.off("updatefindcontrolstate", matchesCountChanged)
            pdfRef.current?.loadingTask?.destroy()
            pdfRef.current = null
        }
    }, [])

    return {
        containerRef,
        state,
        loadDocument,
    }
}

